import {ChangeDetectionStrategy,Component,computed,inject,LOCALE_ID,signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {DomSanitizer,Meta,Title} from '@angular/platform-browser';
import {FormBuilder,FormControl,Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DOCUMENT} from '@angular/common';
import {AppCheckTokenResult,getToken} from 'firebase/app-check';
import {finalize} from 'rxjs';
import {AppService} from '../app.service';
import {ContactService} from './contact.service';
import {SnackMessageComponent} from '../misc/components/snack-message/snack-message';
import {MATERIAL_IMPORTS} from '../shared/material-imports';
import {COMMON_IMPORTS} from '../shared/common-imports';
import {InputEmptyToNullDirective} from '../misc/directives/input-empty-to-null/input-empty-to-null.directive';
import {InputTrimDirective} from '../misc/directives/input-trim/input-trim.directive';
import {environment} from '../../environments/environment';
import {PHONE_NUMBER_UTIL} from '../common/phone-number/phone-number.tokens';
import {FIREBASE_APP_CHECK,FIREBASE_AUTH} from '../common/firebase/firebase.tokens';

@Component({
	selector:'app-contact',
	imports:[
		...MATERIAL_IMPORTS,
		...COMMON_IMPORTS,
		InputEmptyToNullDirective,
		InputTrimDirective
	],
	templateUrl:'./contact.html',
	styleUrl:'./contact.scss',
	changeDetection:ChangeDetectionStrategy.OnPush
})
export class ContactComponent{
	private readonly document=inject(DOCUMENT);
	private readonly formBuilder=inject(FormBuilder);
	private readonly matSnackBar=inject(MatSnackBar);
	private readonly contactService=inject(ContactService);
	readonly appService=inject(AppService);
	readonly auth=inject(FIREBASE_AUTH);
	readonly appCheck=inject(FIREBASE_APP_CHECK);
	readonly phoneNumberUtil=inject(PHONE_NUMBER_UTIL);
	readonly sanitizer=inject(DomSanitizer);
	private readonly localeId=inject(LOCALE_ID);
	private readonly meta=inject(Meta);
	private readonly titleService=inject(Title);
	protected readonly environment=environment;
	/**
	 * The offices, as one list the template loops over instead of repeating a block per city.
	 *
	 * An iframe `src` is a RESOURCE_URL, so Angular rejects any bound value that is not explicitly
	 * trusted; the embed URLs are marked once here rather than in the template, where the call
	 * would re-run on every change detection.
	 */
	readonly offices=[environment.contact.bogota,environment.contact.ibague].map((office)=>({
		...office,
		embedUrl:this.sanitizer.bypassSecurityTrustResourceUrl(office.embedUrl)
	}));
	
	readonly title=$localize`:contact title:L&O Medical - Contacto`;
	readonly form=this.formBuilder.group({
		name:new FormControl<string|null>(null,{validators:[Validators.required,Validators.maxLength(100)]}),
		city:new FormControl<string|null>(null,{validators:[Validators.required,Validators.maxLength(100)]}),
		countryCode:new FormControl<string>('CO',{nonNullable:true,validators:[Validators.required]}),
		phone:new FormControl<string|null>(null,{validators:[Validators.required,Validators.maxLength(20)]}),
		email:new FormControl<string|null>(null,{validators:[Validators.required,Validators.email,Validators.maxLength(254)]}),
		message:new FormControl<string|null>(null,{validators:[Validators.required,Validators.maxLength(2000)]})
	});
	readonly countries:{region:string;code:number;name:string}[];
	submitButtonActive=signal<boolean>(true);
	
	/**
	 * Dial code of the selected region. Derived from the control's value stream rather than read
	 * through a getter, so it recomputes when the selection changes instead of on every change
	 * detection pass.
	 */
	private readonly selectedRegion=toSignal(this.form.controls.countryCode.valueChanges,{
		initialValue:this.form.controls.countryCode.value
	});
	readonly selectedCountryCode=computed<number>(
		()=>this.phoneNumberUtil.getCountryCodeForRegion(this.selectedRegion())
	);
	
	constructor(){
		this.auth.languageCode=this.localeId;
		const displayNames=new Intl.DisplayNames([this.localeId],{type:'region'});
		this.countries=this.phoneNumberUtil.getSupportedRegions()
		.map((region:string)=>({
			region,
			code:this.phoneNumberUtil.getCountryCodeForRegion(region),
			name:displayNames.of(region) ?? region
		}))
		.sort((a,b)=>a.name.localeCompare(b.name));

		const description=$localize`:contact description:Estamos trabajando día a día para ofrecerte un mejor servicio, abrimos nuevos canales de comunicación para brindarte una atención oportuna según tus necesidades.`;
		this.titleService.setTitle(this.title);
		this.meta.updateTag({content:this.title},'property="og:title"');
		this.meta.updateTag({content:description},'name="description"');
		this.meta.updateTag({content:description},'property="og:description"');
		this.meta.updateTag({content:$localize`:contact keywords:lyo, l&o, medical, medicina, biorreguladora, sueroterapia, autosanguis, bogotá, soacha, ibagué`},'name="keywords"');
		this.meta.updateTag({content:this.appService.ogImageUrl},'property="og:image"');
	}

	async onSubmit():Promise<void>{
		if(this.submitButtonActive()){
			const {countryCode,name,city,phone,email,message}=this.form.getRawValue();
			const isNotValid=!name || !city || !phone || !email || !message;
			if(!this.form.valid || isNotValid){
				this.form.markAllAsTouched();
			}else{
				let appCheckTokenResult:AppCheckTokenResult;
				try{
					appCheckTokenResult=await getToken(this.appCheck,true);
				}catch(error:unknown){
					this.matSnackBar.openFromComponent(SnackMessageComponent,{
						data:{error},
						duration:0
					});
					return;
				}
				this.submitButtonActive.set(false);
				this.document.defaultView?.scrollTo(0,0);
				const code=this.phoneNumberUtil.getCountryCodeForRegion(countryCode);
				this.contactService
				.createContactRequest({
					name,
					city,
					email,
					message,
					phone:`+${code}${phone}`,
					recaptchaToken:appCheckTokenResult.token
				})
				.pipe(
					finalize(():void=>{
						this.submitButtonActive.set(true);
					})
				)
				.subscribe({
					next:():void=>{
						this.form.disable();
						this.matSnackBar.openFromComponent(SnackMessageComponent,{
							data:{response:$localize`:contact success:Tu mensaje ha sido enviado con éxito`}
						});
					},
					error:(error:unknown):void=>{
						this.matSnackBar.openFromComponent(SnackMessageComponent,{
							data:{error},
							duration:0
						});
					}
				});
			}
		}
	}
}
